import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export interface IndustryData {
    id: string;
    name: string;
    scenarios: string[];
}

interface IndustryContextType {
    industries: string[];
    scenarios: Record<string, string[]>;
    industryData: IndustryData[];
    loading: boolean;
    refreshIndustries: () => Promise<void>;
}

const IndustryContext = createContext<IndustryContextType>({
    industries: [],
    scenarios: {},
    industryData: [],
    loading: true,
    refreshIndustries: async () => {},
});

export const useIndustries = () => useContext(IndustryContext);

const defaultIndustries: Record<string, string[]> = {
    "Telecom": ["Billing Dispute", "Service Outage", "Plan Upgrade", "Technical Support", "Cancellation Request", "General Inquiry"],
    "Retail": ["Order Status", "Return/Exchange", "Product Inquiry", "Complaint", "Membership/Loyalty", "General Inquiry"],
    "Healthcare": ["Appointment Scheduling", "Insurance Inquiry", "Prescription Refill", "Symptom Check", "Billing Question", "General Inquiry"],
    "Tech": ["Hardware Issue", "Software Bug", "Installation Help", "Account Access", "Feature Request", "General Inquiry"],
    "Finance": ["Fraud Alert", "Loan Application", "Transaction Dispute", "Account Balance", "Investment Advice", "General Inquiry"],
    "Sales": ["Product Demo", "Pricing Negotiation", "Contract Renewal", "Lead Qualification", "Objection Handling", "General Inquiry"],
    "Hospitality": ["Booking Modification", "Check-in Issue", "Special Request", "Complaint Resolution", "Loyalty Rewards", "General Inquiry"],
    "Travel": ["General Inquiry", "Payments", "Tours", "Transfers", "Accommodations", "Baggage Allowance"]
};

export const IndustryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [industryData, setIndustryData] = useState<IndustryData[]>([]);
    const [industries, setIndustries] = useState<string[]>([]);
    const [scenarios, setScenarios] = useState<Record<string, string[]>>({});
    const [loading, setLoading] = useState(true);

    const refreshIndustries = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'industries'));
            
            if (snap.empty) {
                // Collection is empty — use built-in defaults for display.
                // Do NOT attempt to write; non-admin users lack write permission
                // and the silent failure was causing an empty dropdown.
                console.log("No industries found in Firestore, using built-in defaults for display.");
                const newInds: IndustryData[] = Object.entries(defaultIndustries).map(([indName, indScenarios]) => ({
                    id: indName.toLowerCase().replace(/\s+/g, '-'),
                    name: indName,
                    scenarios: indScenarios,
                }));
                newInds.sort((a, b) => a.name.localeCompare(b.name));

                setIndustryData(newInds);
                setIndustries(newInds.map(i => i.name));
                const scenariosMap: Record<string, string[]> = {};
                newInds.forEach(i => { scenariosMap[i.name] = i.scenarios; });
                setScenarios(scenariosMap);
            } else {
                const data: IndustryData[] = snap.docs.map(d => ({
                    id: d.id,
                    name: d.data().name || d.id,
                    scenarios: d.data().scenarios || []
                }));
                // Sort by name alphabetically
                data.sort((a, b) => a.name.localeCompare(b.name));
                
                setIndustryData(data);
                setIndustries(data.map(i => i.name));
                const scenariosMap: Record<string, string[]> = {};
                data.forEach(i => { scenariosMap[i.name] = i.scenarios; });
                setScenarios(scenariosMap);
            }
        } catch (err) {
            // Fetch failed (e.g. network error, permissions not yet ready).
            // Fall back to built-in defaults so the dropdown is never empty.
            console.error('Failed to fetch industries, falling back to defaults:', err);
            const fallbackInds: IndustryData[] = Object.entries(defaultIndustries).map(([indName, indScenarios]) => ({
                id: indName.toLowerCase().replace(/\s+/g, '-'),
                name: indName,
                scenarios: indScenarios,
            }));
            fallbackInds.sort((a, b) => a.name.localeCompare(b.name));
            setIndustryData(fallbackInds);
            setIndustries(fallbackInds.map(i => i.name));
            const scenariosMap: Record<string, string[]> = {};
            fallbackInds.forEach(i => { scenariosMap[i.name] = i.scenarios; });
            setScenarios(scenariosMap);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshIndustries();
    }, []);

    return (
        <IndustryContext.Provider value={{ industries, scenarios, industryData, loading, refreshIndustries }}>
            {children}
        </IndustryContext.Provider>
    );
};
